/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2024, Zumper
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import { logger } from '@zcode/utils'
import type { ErrorBoundaryProps } from 'react-error-boundary'

const fallbackStyle = css`
  height: 900px;
  @media (max-width: ${BREAKPOINT_MOBILE}) {
    height: 400px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for HomepageSpotlightListings lazy loadable section', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for HomepageSpotlightListings lazy loadable section', error)
}

const loadFn = /* #__LOADABLE__ */ async () => await import('./HomepageSpotlightListings')

const handleReload = async () => await HomepageSpotlightListingsLazyLoadableSection.reload()

const options = {
  errorFallback: () => (
    <ErrorFallback
      css={fallbackStyle}
      message="Error loading spotlight listings"
      onClickReload={handleReload}
    />
  ),

  loadingFallback: () => <LoadingFallback css={fallbackStyle} name="spotlight listings" />,
  onRenderError,
  onLoadingError,
}

export const HomepageSpotlightListingsLazyLoadableSection = createLazyLoadableSection(
  loadFn,
  options
)
